import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import { CustomHeader } from "../components/AuthHeader";
import { CustomButton } from "../components/CustomButton";
import { useAuthStore } from "../features/auth/authStore";
import { authService } from "../features/auth/authService";
import { RegisterInput } from "../features/auth/types";
import { useToast } from "../components/ToastProvider";
import rushLogo from "../assets/png/rush-logo.png";

type VerifyLocationState = {
  registrationData?: RegisterInput;
};

/**
 * Sign-up step 2: the customer enters the 6-digit code we emailed them. The
 * account is created (and signed in) only once the code checks out.
 */
export default function OTPVerifyScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const customerRegister = useAuthStore((s) => s.customerRegister);
  const isLoading = useAuthStore((s) => s.isLoading);

  const registrationData = (location.state as VerifyLocationState | null)?.registrationData;
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);

  // Opened directly, without coming from the sign-up form
  if (!registrationData) return <Navigate to="/signup" replace />;

  const handleCreateAccount = async () => {
    if (code.length !== 6) {
      showToast("Enter the 6-digit code from the email", "error");
      return;
    }
    try {
      await customerRegister({ ...registrationData, emailCode: code });
      showToast("Welcome to RUSH! Your account is ready", "success");
      navigate("/home", { replace: true });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Verification failed", "error");
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authService.requestSignupCode(registrationData.email, registrationData.firstName);
      setCode("");
      showToast("We've sent you a new code", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Couldn't send a new code", "error");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen md:min-h-0 bg-white px-6 pt-4 pb-10">
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md px-6 -mx-6 pt-4 pb-4 flex items-center justify-between mb-2">
        <button
          onClick={() => navigate("/signup")}
          className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center active:scale-95 transition-transform shadow-sm bg-white"
          aria-label="Back to sign up"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <img src={rushLogo} alt="Rush Logo" className="h-7 w-auto object-contain" />
      </div>

      <CustomHeader title="Confirm your email" subtitle={`Enter the 6-digit code we sent to ${registrationData.email}.`} />

      <div className="mt-4 flex gap-3 rounded-2xl bg-[#44B6CE]/10 px-4 py-3">
        <MailCheck size={18} className="text-[#1A8FAA] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          The code expires in 10 minutes. Check your spam folder if you can&apos;t see it.
        </p>
      </div>

      <form
        className="mt-6 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void handleCreateAccount();
        }}
      >
        <div>
          <label
            htmlFor="signup-code"
            className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block"
          >
            6-digit code
          </label>
          <input
            id="signup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            disabled={isLoading}
            className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-center text-2xl font-semibold tracking-[0.5em] text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all disabled:opacity-50"
          />
        </div>

        <CustomButton
          type="submit"
          variant="primary"
          disabled={isLoading || code.length !== 6}
          className="w-full h-14 text-lg rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform shadow-lg shadow-cyan-100"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Creating account...</span>
            </>
          ) : (
            <span>Create Account</span>
          )}
        </CustomButton>

        <p className="text-center text-sm text-gray-500">
          Didn&apos;t get it?{" "}
          <button
            type="button"
            onClick={() => void handleResend()}
            disabled={resending || isLoading}
            className="text-[#23A9C6] font-bold hover:underline disabled:opacity-50"
          >
            {resending ? "Sending..." : "Send a new code"}
          </button>
        </p>
      </form>
    </div>
  );
}
